import { httpsCallable } from "firebase/functions";

import { functionsClient } from "@/src/lib/firebase";
import type { Subject, Track } from "@/src/lib/taxonomy";
import type { ControlMember, ControlPermission, ControlRole } from "./permissions";
import type {
  AccountType,
  IdentityEntitlements,
  IdentityRequest,
  IdentityRequestStatus,
  PublicIdentitySnapshot,
  VerificationStatus,
} from "../types/identity";

export type ModerationCaseStatus = "open" | "reviewing" | "resolved" | "dismissed";

export type ModerationProfile = {
  uid: string;
  identityId?: string;
  username?: string;
  displayName?: string;
  institutionName?: string;
  /** @deprecated Compatibility alias for historical profiles. */
  role?: "student" | "teacher";
  accountType?: AccountType;
  verificationStatus?: VerificationStatus;
  entitlements?: IdentityEntitlements;
  teacherVerified?: boolean;
  photoURL?: string;
  badges?: { kind: string; label: string }[];
};

export type ModerationPost = {
  id: string;
  authorUid: string;
  text: string;
  subject?: string;
  visibility?: "global" | "group";
  groupId?: string;
  groupName?: string;
  moderationStatus?: string;
  images?: {
    url: string;
    width?: number;
    height?: number;
    contentType?: string;
  }[];
  attachmentType?: "image" | "pdf";
  attachmentUrl?: string;
  attachmentName?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type ModerationCaseSummary = {
  id: string;
  targetType: string;
  targetId: string;
  postId?: string;
  reportedUserUid?: string;
  reportedIdentityId?: string;
  reportedIdentityType?: AccountType;
  reportedIdentitySnapshot?: PublicIdentitySnapshot | null;
  scope?: string;
  conversationId?: string;
  messageId?: string;
  source?: string;
  status: ModerationCaseStatus;
  severity?: "normal" | "high" | "critical";
  reportCount?: number;
  uniqueReporterCount?: number;
  reason?: string;
  details?: string;
  latestReporterUid?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
  latestReportCreatedAt?: unknown;
  author?: ModerationProfile | null;
  reporter?: ModerationProfile | null;
  autoModerationResult?: {
    decision?: string;
    reasonCodes?: string[];
    flagged?: boolean;
  };
};

export type ModerationReport = {
  id: string;
  targetType: string;
  targetId: string;
  postId?: string;
  groupId?: string;
  reporterUid: string;
  reportedUserUid?: string;
  reporterIdentityId?: string;
  reportedIdentityId?: string;
  reportedIdentityType?: AccountType;
  reportedIdentitySnapshot?: PublicIdentitySnapshot | null;
  scope?: string;
  conversationId?: string;
  messageId?: string;
  reason: string;
  details?: string;
  severity?: string;
  assignedTo?: string;
  status: string;
  createdAt?: unknown;
  updatedAt?: unknown;
  reporter?: ModerationProfile | null;
};

export type ModerationActionRecord = {
  id: string;
  caseId: string;
  targetType?: string;
  targetId?: string;
  targetUid?: string;
  targetUserUid?: string;
  targetIdentityId?: string;
  targetIdentityType?: AccountType;
  action: string;
  type?: string;
  reason?: string;
  internalReason?: string;
  createdBy?: string;
  moderatorUid?: string;
  status?: string;
  expiresAt?: unknown;
  createdAt?: unknown;
};

export type ModerationCaseDetail = {
  case: ModerationCaseSummary;
  content: {
    type: "post" | "comment" | "user" | "direct_message" | "group_message";
    post?: ModerationPost;
    comment?: { id: string; postId: string; text: string; authorUid: string; createdAt?: unknown };
    profile?: ModerationProfile;
    conversationId?: string;
    groupId?: string;
    messageId?: string;
    message?: {
      type: "text" | "shared_post";
      text: string;
      senderIdentityId: string;
      senderSnapshot?: PublicIdentitySnapshot | null;
      sharedPost?: Record<string, unknown>;
    };
  } | null;
  author: ModerationProfile | null;
  moderationState: {
    accountStatus?: string;
    postingBlockedUntil?: unknown;
    expiresAt?: unknown;
    sanctionType?: string;
    reasonCode?: string;
  } | null;
  reports: ModerationReport[];
  actions: ModerationActionRecord[];
};

export type SponsoredCampaignStatus = "draft" | "active" | "paused" | "ended";
export type SponsoredModerationStatus = "pending" | "approved" | "rejected";
export type SponsoredDeliveryState = "draft" | "paused" | "blocked" | "expired" | "scheduled" | "delivering" | "ended";

export type SponsoredCampaignInput = {
  advertiserName: string;
  headline?: string;
  body: string;
  mediaUrl?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  targetSubjects?: Subject[];
  targetTracks?: Track[];
  startsAt: string;
  endsAt?: string;
  frequencyCap: {
    maxImpressions: number;
    windowHours: number;
  };
};

export type SponsoredCampaignAdmin = {
  id: string;
  advertiserName: string;
  status: SponsoredCampaignStatus;
  moderationStatus: SponsoredModerationStatus;
  moderationReason?: string;
  headline?: string;
  body: string;
  mediaUrl?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  placement: "feed";
  targetSubjects: Subject[];
  targetTracks: Track[];
  startsAt?: unknown;
  endsAt?: unknown;
  frequencyCap: {
    maxImpressions: number;
    windowHours: number;
  };
  deliveryState: SponsoredDeliveryState;
  createdAt?: unknown;
  updatedAt?: unknown;
  createdBy?: string;
  updatedBy?: string;
  reviewedAt?: unknown;
  reviewedBy?: string;
};

export type SponsoredCampaignMetrics = {
  impressions: number;
  clicks: number;
  ctr: number;
  truncated: boolean;
};

export type ControlDashboard = {
  member: ControlMember;
  metrics: {
    activeControlMembers: number;
    openModerationCases: number;
    activeSponsoredCampaigns: number;
  };
};

export type IdentityRequester = {
  uid: string;
  displayName?: string;
  username?: string;
  accountType?: AccountType | null;
  verificationStatus?: VerificationStatus;
  mainSubject?: string;
  track?: string;
  photoURL?: string;
};

export type IdentityRequestView = {
  request: IdentityRequest;
  requester: IdentityRequester | null;
};

export type IdentityRequestListStatus = IdentityRequestStatus | "all";

export type ControlAuditEvent = {
  id: string;
  action: string;
  actorUid: string | null;
  actorRole?: ControlRole | "platformModerator";
  targetType?: string;
  targetId?: string;
  reason?: string;
  reportId?: string;
  previousState?: string;
  newState?: string;
  metadata?: Record<string, unknown>;
  source: "control_center" | "system" | "bootstrap";
  createdAt?: unknown;
};

export async function getControlSession() {
  const callable = httpsCallable<Record<string, never>, { member: ControlMember }>(functionsClient, "getControlSession");
  return (await callable({})).data;
}

export async function getControlDashboard() {
  const callable = httpsCallable<Record<string, never>, ControlDashboard>(functionsClient, "getControlDashboard");
  return (await callable({})).data;
}

export async function listControlMembers() {
  const callable = httpsCallable<Record<string, never>, { members: ControlMember[] }>(functionsClient, "listControlMembers");
  return (await callable({})).data.members;
}

export async function upsertControlMember(input: { uid: string; role: ControlRole; permissions?: ControlPermission[] }) {
  const callable = httpsCallable<typeof input, { ok: true }>(functionsClient, "upsertControlMember");
  return (await callable(input)).data;
}

export async function disableControlMember(uid: string) {
  const callable = httpsCallable<{ uid: string }, { ok: true }>(functionsClient, "disableControlMember");
  return (await callable({ uid })).data;
}

export async function listControlAuditLog(limit = 50) {
  const callable = httpsCallable<{ limit: number }, { events: ControlAuditEvent[] }>(functionsClient, "listControlAuditLog");
  return (await callable({ limit })).data.events;
}

export async function listIdentityRequests(
  status: IdentityRequestListStatus = "pending",
  limit = 50
) {
  const callable = httpsCallable<
    { status: IdentityRequestListStatus; limit: number },
    { requests: IdentityRequestView[] }
  >(functionsClient, "listIdentityRequests");
  return (await callable({ status, limit })).data.requests;
}

export async function getIdentityRequest(requestId: string) {
  const callable = httpsCallable<{ requestId: string }, IdentityRequestView>(functionsClient, "getIdentityRequest");
  return (await callable({ requestId })).data;
}

export async function decideIdentityRequest(
  requestId: string,
  decision: Exclude<IdentityRequestStatus, "pending" | "cancelled">,
  decisionNote?: string
) {
  const callable = httpsCallable<
    { requestId: string; decision: "approved" | "rejected"; decisionNote?: string },
    { request: IdentityRequest; institutionIdentityId?: string }
  >(functionsClient, "decideIdentityRequest");
  return (await callable({ requestId, decision, ...(decisionNote ? { decisionNote } : {}) })).data;
}

export async function listModerationCases(
  status: ModerationCaseStatus = "open",
  limit = 50
) {
  const callable = httpsCallable<
    { status: ModerationCaseStatus; limit: number },
    { cases: ModerationCaseSummary[] }
  >(functionsClient, "listModerationCases");
  return (await callable({ status, limit })).data.cases;
}

export async function getModerationCase(caseId: string) {
  const callable = httpsCallable<{ caseId: string }, ModerationCaseDetail>(functionsClient, "getModerationCase");
  return (await callable({ caseId })).data;
}

export async function claimModerationCase(caseId: string) {
  const callable = httpsCallable<{ caseId: string }, { ok: true }>(functionsClient, "claimModerationCase");
  return (await callable({ caseId })).data;
}

export async function listSponsoredCampaigns(status: SponsoredCampaignStatus | "all" = "all") {
  const callable = httpsCallable<
    { status: SponsoredCampaignStatus | "all" },
    { campaigns: SponsoredCampaignAdmin[] }
  >(functionsClient, "listSponsoredCampaigns");
  return (await callable({ status })).data.campaigns;
}

export async function getSponsoredCampaign(campaignId: string) {
  const callable = httpsCallable<
    { campaignId: string },
    { campaign: SponsoredCampaignAdmin; metrics: SponsoredCampaignMetrics }
  >(functionsClient, "getSponsoredCampaign");
  return (await callable({ campaignId })).data;
}

export async function createSponsoredCampaign(campaign: SponsoredCampaignInput) {
  const callable = httpsCallable<
    { campaign: SponsoredCampaignInput },
    { ok: true; campaignId: string }
  >(functionsClient, "createSponsoredCampaign");
  return (await callable({ campaign })).data;
}

export async function updateSponsoredCampaign(campaignId: string, campaign: SponsoredCampaignInput) {
  const callable = httpsCallable<
    { campaignId: string; campaign: SponsoredCampaignInput },
    { ok: true }
  >(functionsClient, "updateSponsoredCampaign");
  return (await callable({ campaignId, campaign })).data;
}

export async function setSponsoredCampaignStatus(campaignId: string, status: SponsoredCampaignStatus) {
  const callable = httpsCallable<
    { campaignId: string; status: SponsoredCampaignStatus },
    { ok: true }
  >(functionsClient, "setSponsoredCampaignStatus");
  return (await callable({ campaignId, status })).data;
}

export async function reviewSponsoredCampaign(
  campaignId: string,
  decision: Exclude<SponsoredModerationStatus, "pending">,
  reason: string
) {
  const callable = httpsCallable<
    { campaignId: string; decision: Exclude<SponsoredModerationStatus, "pending">; reason: string },
    { ok: true }
  >(functionsClient, "reviewSponsoredCampaign");
  return (await callable({ campaignId, decision, reason })).data;
}
